import type { AppLocale } from "@/lib/locale/resolve-effective-locale";
import { WarningColors } from "@/features/shared/widgets/types";
import type { WarningColor } from "@/features/shared/widgets/components/time-series-table/components/types";

export const getSeichesColorCodeForValue = (value: number | string): WarningColor => {
  if (typeof value !== "number") return WarningColors.NONE;
  if (value >= 0 && value <= 0.99) return WarningColors.SUCCESS;
  if (value > 0.99 && value < 100) return WarningColors.ERROR;
  return WarningColors.NONE;
};

const SEICHES_SUBTYPE_LABELS = new Set(["A", "B", "C", "D"]);

/** Maps API seiches subtype (e.g. "a") to display label A–D, or null when absent/invalid. */
export function normalizeSeichesSubtypeLabel(subtype: string | undefined): string | null {
  if (!subtype) return null;
  const label = subtype.trim().toUpperCase();
  return SEICHES_SUBTYPE_LABELS.has(label) ? label : null;
}

export const formatSeichesCellValue = (
  value: number,
  t: (key: string, options?: Record<string, string>) => string,
  subtype?: string | null,
): string => {
  if (!value) return t("widgets.seiches.none");

  const normalizedSubtype =
    subtype === undefined ? null : normalizeSeichesSubtypeLabel(subtype ?? undefined);

  if (subtype === undefined) {
    return t("widgets.seiches.yes");
  }

  if (normalizedSubtype) {
    return t("widgets.seiches.yes_with_subtype", { subtype: normalizedSubtype });
  }

  return t("widgets.seiches.yes_no_data");
};

export const formatSeichesTooltip = (
  value: number,
  t: (key: string, options?: Record<string, string>) => string,
  message?: string | null,
  subtype?: string | null,
): string => {
  if (value > 0.99 && message) return message;
  return formatSeichesCellValue(value, t, subtype);
};

/** Resolves localized seiches message for tooltip display. */
export function resolveSeichesMessage(
  message: { en?: string; nl?: string } | undefined,
  locale: AppLocale,
): string | null {
  if (!message) return null;
  if (locale === "en") return message.en ?? message.nl ?? null;
  return message.nl ?? message.en ?? null;
}
